<template>
  <FriedModal size="xxlarge" :class="{ 'naming-modal': showNamingModal }" @close="$emit('close')">
    <template v-if="showNamingModal" #container>
      <div v-if="creating" class="loading-overlay">
        <FriedSpinner />
      </div>
      <DsrNamingSubContainer
        :template-name="templateName"
        @back="resetNamingModal"
        @create-room="createRoom"
      />
    </template>
    <div v-if="!showNamingModal">
      <FriedModalHeader :header="$t('create-your-digital-sales-room')" />
      <div class="content">
        <FriedButton
          class="create-button"
          compact
          data-external="create-dsr-without-template-button"
          @click="showNamingModal = true"
        >
          {{ $t('start-off-fresh') }}
          <template #leftIcon><FriedIcon icon="add" /></template>
        </FriedButton>
        <template v-if="lastUsedTemplates.length">
          <FriedH5>{{ $t('recently-used') }}</FriedH5>
          <div class="recent-container">
            <LastUsedCard
              v-for="template in lastUsedTemplates"
              :key="template.id"
              :template="template"
              @click="selectTemplate({ id: template.contentId, name: template.title })"
            />
          </div>
        </template>
        <div class="search-container">
          <SearchContent
            :searching="searching"
            class="search-bar"
            :placeholder="$t('search-for-templates')"
            @search="handleSearch"
          />
          <TagFilterButton
            v-if="flags.drTags && hasTags"
            :categories="categories"
            :selected-tags="selectedTags"
            @update-tags="setSelectedTags"
          />
        </div>
        <TemplateBreadcrumbs
          v-show="currentDirectory !== DsrDirectoryConstants.RootPath"
          class="breadcrumbs"
          :path="currentDirectory"
          @goto-directory="loadByDirectory"
        />
        <div class="table-wrapper">
          <TemplateTable
            :templates="templates"
            :directories="directories"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :loading="loading"
            :has-tag-flag="flags.drTags"
            :categories="categories"
            :error="error"
            :has-next-page="hasNextPage"
            @goto-directory="loadByDirectory"
            @create-from-template="selectTemplate"
            @sort="handleSort"
            @fetch-more-templates="loadMoreResources(selectedTags)"
          />
        </div>
      </div>
    </div>
  </FriedModal>
</template>

<script lang="ts" setup>
import type { Ref } from 'vue';
import { onMounted, ref, computed } from 'vue';
import { EnumDocumentType } from '@getaccept/lib-shared/src/enums/DocumentType';
import type { SortOrder } from '@getaccept/fried-tofu';
import SearchContent from '@getaccept/lib-shared/src/components/SearchContent.vue';
import { DsrDirectoryConstants } from '@getaccept/lib-shared/src/library/constants/dsr-directory.constants';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import { useLaunchDarkly } from '@getaccept/lib-shared/src/launch-darkly/launch-darkly.composable';
import { ContentTagHelper, TagFilterButton } from '@getaccept/dsr-shared';
import type { DsrSortAttribute } from '../api/dsr/types/enums/dsr-sort-attribute';
import { DsrService } from '../api/dsr/services/dsr.service';
import type {
  CreateRoomFromTemplatePayload,
  CreateRoomPayload,
} from '../api/dsr/types/create-room-payload';
import { useContentTags } from '../settings/dealroom-tags/composables/content-tag.composable';
import DsrNamingSubContainer from './DsrNamingSubContainer.vue';
import { useLoadTemplates } from './composables/load-templates.composable';
import TemplateBreadcrumbs from './TemplateBreadcrumbs.vue';
import LastUsedCard from './LastUsedCard.vue';
import TemplateTable from './TemplateTable.vue';

const emit = defineEmits<{
  'dsr-created': [{ id: string; type: EnumDocumentType }];
  close: [];
}>();

const {
  loadLastUsed,
  loadByDirectory,
  loadMoreResources,
  searchTemplates,
  hasNextPage,
  lastUsedTemplates,
  loading,
  error,
  currentDirectory,
  templates,
  directories,
  searching,
  setSort,
  sortOrder,
  sortBy,
} = useLoadTemplates();

const latestSearch = ref('');
const showNamingModal = ref(false);
const templateName = ref('');
const templateId = ref('');
const creating = ref(false);
const { flags } = useLaunchDarkly();
const { categories, load: loadContentTags } = useContentTags();

const hasTags = computed(() => ContentTagHelper.hasTags(categories.value));

const useTemplate = async (payload: CreateRoomFromTemplatePayload, collaborators: User[]) => {
  creating.value = true;

  try {
    const dsrId = await DsrService.useTemplate(payload);
    collaborators.forEach(collaborator => {
      DsrService.createCollaborator(dsrId, collaborator.id);
    });
    emit('dsr-created', { id: dsrId, type: EnumDocumentType.DSR });
  } catch (e) {
    console.error('Could not use template', e);
  } finally {
    creating.value = false;
  }
};

const createDsr = async ({ name, companyName, collaborators }: CreateRoomPayload) => {
  creating.value = true;
  try {
    const id = await DsrService.createDealroom({
      name,
      companyName,
    });
    collaborators.forEach(collaborator => {
      DsrService.createCollaborator(id, collaborator.id);
    });
    emit('dsr-created', { id, type: EnumDocumentType.DSR });
  } catch (e) {
    console.error('Could not create dsr', e);
  } finally {
    creating.value = false;
  }
};

const createRoom = ({ name, companyName, collaborators }: CreateRoomPayload) => {
  if (templateId.value) {
    useTemplate({ id: templateId.value, name, companyName }, collaborators);
    return;
  }

  createDsr({ name, companyName, collaborators });
};

const selectTemplate = async ({ id, name }: { id: string; name: string }) => {
  showNamingModal.value = true;
  templateId.value = id;
  templateName.value = name;
};

const resetNamingModal = () => {
  latestSearch.value = '';
  currentDirectory.value = DsrDirectoryConstants.RootPath;
  loadByDirectory(currentDirectory.value);
  showNamingModal.value = false;
  templateId.value = '';
  templateName.value = '';
};

const selectedTags: Ref<string[]> = ref([]);

const handleSearch = (searchTerm: string) => {
  latestSearch.value = searchTerm;

  if (searchTerm || selectedTags.value.length) {
    searchTemplates(searchTerm, selectedTags.value);
  } else {
    loadByDirectory(currentDirectory.value);
  }
};

const handleSort = (sort: { sortBy: DsrSortAttribute; sortOrder: SortOrder }) => {
  setSort(sort.sortBy, sort.sortOrder);
  handleSearch(latestSearch.value);
};

const setSelectedTags = (tagIds: string[]) => {
  selectedTags.value = tagIds;
  if (!selectedTags.value.length) {
    handleSearch(latestSearch.value);
  } else {
    searchTemplates(latestSearch.value, selectedTags.value);
  }
};

onMounted(() => {
  loadLastUsed();
  loadByDirectory();
  loadContentTags();
});
</script>

<style lang="scss" scoped>
@import '@getaccept/dsr-shared/src/content-tags/scss/search-tag-filter';

.content {
  display: flex;
  flex-direction: column;
  height: 600px;

  .create-button {
    margin-top: var(--spacing-100);
    margin-bottom: var(--spacing-150);
    width: fit-content;
  }

  .recent-container {
    margin-top: var(--spacing-100);
    margin-bottom: var(--spacing-150);
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: var(--spacing-100);
    border-inline: 2px solid transparent;
  }

  .search-bar {
    margin-bottom: var(--spacing-100);
  }

  .breadcrumbs {
    margin-bottom: var(--spacing-100);
    overflow: visible;
  }

  .table-wrapper {
    overflow: auto;

    @media screen and (max-width: $xs) {
      overflow-x: hidden;
    }
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-medium);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}
</style>
<style lang="scss">
.naming-modal {
  &.modal-outer-container .modal-container {
    height: 800px;
    overflow: hidden;
  }
}
</style>
